import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useDialog } from "../../../components/DialogContext";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateUserProfile } from "../../../redux/Apps/users/userSlice";

const allCurrencies = [
  {
    id: 1,
    name: "United States Dollar",
    symbol: "$",
    shortForm: "USD",
    isDefault: false,
  },
  {
    id: 2,
    name: "Euro",
    symbol: "€",
    shortForm: "EUR",
    isDefault: false,
  },
  {
    id: 3,
    name: "British Pound",
    symbol: "£",
    shortForm: "GBP",
    isDefault: false,
  },
  {
    id: 4,
    name: "United Arab Emirates Dirham",
    symbol: "د.إ",
    shortForm: "AED",
    isDefault: false,
  },
  {
    id: 5,
    name: "Afghan Afghani",
    symbol: "؋",
    shortForm: "AFN",
    isDefault: false,
  },
  {
    id: 6,
    name: "Albanian Lek",
    symbol: "Lek",
    shortForm: "ALL",
    isDefault: false,
  },
  {
    id: 7,
    name: "Armenian Dram",
    symbol: "AMD",
    shortForm: "AMD",
    isDefault: false,
  },
  {
    id: 8,
    name: "Netherlands Antillean Guilder",
    symbol: "ƒ",
    shortForm: "ANG",
    isDefault: false,
  },
  {
    id: 9,
    name: "Argentine Peso",
    symbol: "$",
    shortForm: "ARS",
    isDefault: false,
  },
  {
    id: 10,
    name: "Australian Dollar",
    symbol: "$",
    shortForm: "AUD",
    isDefault: false,
  },
  {
    id: 11,
    name: "Aruban Florin",
    symbol: "ƒ",
    shortForm: "AWG",
    isDefault: false,
  },
  {
    id: 12,
    name: "Azerbaijani Manat",
    symbol: "ман",
    shortForm: "AZN",
    isDefault: false,
  },
  {
    id: 13,
    name: "Bangladeshi Taka",
    symbol: "৳",
    shortForm: "BDT",
    isDefault: false,
  },
  {
    id: 14,
    name: "Bulgarian Lev",
    symbol: "лв",
    shortForm: "BGN",
    isDefault: false,
  },
  {
    id: 15,
    name: "Bahraini Dinar",
    symbol: "BHD",
    shortForm: "BHD",
    isDefault: false,
  },
  {
    id: 16,
    name: "Brazilian Real",
    symbol: "R$",
    shortForm: "BRL",
    isDefault: false,
  },
  {
    id: 17,
    name: "Swiss Franc",
    symbol: "CHF",
    shortForm: "CHF",
    isDefault: false,
  },
  {
    id: 18,
    name: "Chinese Yuan",
    symbol: "CN¥",
    shortForm: "CNY",
    isDefault: false,
  },
  {
    id: 19,
    name: "Czech Koruna",
    symbol: "Kč",
    shortForm: "CZK",
    isDefault: false,
  },
  {
    id: 20,
    name: "Danish Krone",
    symbol: "kr",
    shortForm: "DKK",
    isDefault: false,
  },
  {
    id: 21,
    name: "Egyptian Pound",
    symbol: "£",
    shortForm: "EGP",
    isDefault: false,
  },
  {
    id: 22,
    name: "Japanese Yen",
    symbol: "¥",
    shortForm: "JPY",
    isDefault: false,
  },
  {
    id: 23,
    name: "Indian Rupee",
    symbol: "₹",
    shortForm: "INR",
    isDefault: false,
  },
  {
    id: 24,
    name: "South African Rand",
    symbol: "R",
    shortForm: "ZAR",
    isDefault: false,
  },
  {
    id: 25,
    name: "Canadian Dollar",
    symbol: "CA$",
    shortForm: "CAD",
    isDefault: false,
  },
];

export const getCurrencySymbol = (value) => {
  if (!value) return "";

  const normalizedValue = value.toString().toUpperCase();

  const currency = allCurrencies.find(
    (c) =>
      c.shortForm.toUpperCase() === normalizedValue ||
      c.name.toUpperCase() === normalizedValue,
  );

  return currency?.symbol || normalizedValue;
};
const CurrencySetForm = () => {
  const { closeDialog } = useDialog();
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);
  const [makeDefault, setMakeDefault] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const {
    token,
    lawerId,
    UserCurrencyList = [],
    user,
  } = useSelector((state) => state.user);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedCurrencies.length === 0) {
      toast.error("Please select at least one currency");
      return;
    }

    if (makeDefault && !defaultCurrency) {
      toast.error("Please select a default currency");
      return;
    }

    setIsSubmitting(true);

    try {
      const currentExtra = user?.extra || {};

      const currentCurrencies = Array.isArray(UserCurrencyList)
        ? [...UserCurrencyList]
        : [];

      const newCurrencies = selectedCurrencies.map((c) => ({
        ...c,
        isDefault: makeDefault && c.shortForm === defaultCurrency,
      }));

      const mergedCurrencies = [...currentCurrencies];
      const currencySet = new Set(currentCurrencies.map((c) => c.shortForm));

      newCurrencies.forEach((newCurrency) => {
        if (!currencySet.has(newCurrency.shortForm)) {
          mergedCurrencies.push(newCurrency);
          currencySet.add(newCurrency.shortForm);
        }
      });

      if (makeDefault && defaultCurrency) {
        mergedCurrencies.forEach((currency) => {
          currency.isDefault = currency.shortForm === defaultCurrency;
        });
      } else {
        const hasDefault = mergedCurrencies.some((c) => c.isDefault);
        if (!hasDefault && mergedCurrencies.length > 0) {
          mergedCurrencies[0].isDefault = true;
        }
      }

      const rootDefaultCurrency = mergedCurrencies.find((c) => c.isDefault);

      const updatedExtra = {
        ...currentExtra,
        user_currencies: mergedCurrencies,
      };

      const payload = {
        currency: rootDefaultCurrency ? rootDefaultCurrency.shortForm : "",
        extra: updatedExtra,
      };

      await dispatch(updateUserProfile({ token, lawerId, payload })).unwrap();

      toast.success("Currencies updated successfully");
      closeDialog();
    } catch (err) {
      console.error("Error updating currencies:", err);
      toast.error(err.message || "Failed to update currencies");
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableCurrencies = React.useMemo(() => {
    const userCurrencySet = new Set(
      Array.isArray(UserCurrencyList)
        ? UserCurrencyList.map((c) => c.shortForm)
        : [],
    );
    return allCurrencies.filter((c) => !userCurrencySet.has(c.shortForm));
  }, [UserCurrencyList]);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ p: "1rem" }}>
          <Autocomplete
            multiple
            options={availableCurrencies}
            value={selectedCurrencies}
            onChange={(_, newValue) => {
              setSelectedCurrencies(newValue);

              if (newValue.length === 0) {
                setMakeDefault(false);
                setDefaultCurrency("");
              } else if (
                !newValue.some((c) => c.shortForm === defaultCurrency)
              ) {
                setDefaultCurrency(newValue[0].shortForm);
              }
            }}
            isOptionEqualToValue={(option, value) =>
              option.shortForm === value.shortForm
            }
            getOptionLabel={(option) => `${option.shortForm} — ${option.name}`}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                <Box>
                  <Typography fontSize="0.9rem">{option.name}</Typography>
                  <Typography fontSize="0.75rem" color="text.secondary">
                    {option.shortForm} • {option.symbol}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.shortForm}
                  label={`${option.shortForm} ${option.symbol}`}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Currencies"
                size="small"
                placeholder="Search currencies"
                helperText={
                  selectedCurrencies.length
                    ? `${selectedCurrencies.length} currency(s) selected`
                    : availableCurrencies.length === 0
                      ? "All available currencies have been added"
                      : "Start typing to search"
                }
              />
            )}
          />
        </Box>

        {/* STEP 2: Default Checkbox */}
        <Box sx={{ px: "1rem", pb: "1rem" }}>
          <FormControlLabel
            label="Set one as default currency"
            control={
              <Checkbox
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                disabled={selectedCurrencies.length === 0}
                size="small"
              />
            }
          />
        </Box>

        {/* STEP 3: Default Radio */}
        {makeDefault && selectedCurrencies.length > 0 && (
          <Box sx={{ px: "1rem", pb: "1rem" }}>
            <FormControl>
              <FormLabel sx={{ fontWeight: 600, mb: 1 }}>
                Which currency should be the default?
              </FormLabel>

              <RadioGroup
                value={defaultCurrency}
                onChange={(e) => setDefaultCurrency(e.target.value)}
              >
                {selectedCurrencies.map((c) => (
                  <FormControlLabel
                    key={c.shortForm}
                    value={c.shortForm}
                    control={<Radio size="small" />}
                    label={`${c.symbol} ${c.name} (${c.shortForm})`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* ACTIONS */}
        <Box
          sx={{
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            py: 1,
            px: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={closeDialog}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            size="small"
            loading={isSubmitting}
            disabled={selectedCurrencies.length === 0}
          >
            Add Currencies
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};

export default CurrencySetForm;
